import { motion } from '@/lib/motion';
import { ArrowRight, Check, MessageSquare, PhoneCall } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';

/**
 * Two large channel cards (Voice / Chat) — the home → product-page funnel.
 * Voice is brand-accent tinted, chat is emerald, matching the per-page color
 * coding carried through /voice and /chat.
 */
export function ChannelCards(): JSX.Element {
  return (
    <section className="container py-14 sm:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
          Two channels, one platform
        </h2>
        <p className="mt-4 text-muted-foreground">
          Pick a channel to go deep — or run both. Every plan includes them together.
        </p>
      </div>

      <div className="mx-auto mt-14 grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
        <ChannelCard
          tone="accent"
          icon={<PhoneCall className="h-6 w-6" />}
          eyebrow="AI Voice Agent"
          title="Answers every phone call, 24/7"
          bullets={[
            'Sub-second, natural conversation',
            'Captures booking requests + transfers on intent',
            'Local numbers in US, UK, AU, NZ',
          ]}
          to="/voice"
          cta="Explore the voice agent"
        />
        <ChannelCard
          tone="emerald"
          icon={<MessageSquare className="h-6 w-6" />}
          eyebrow="AI Chat Widget"
          title="Answers every website visitor, instantly"
          bullets={[
            'One script tag to install',
            'Streams answers with citations',
            'Per-origin locked + spend-capped',
          ]}
          to="/chat"
          cta="Explore the chat widget"
        />
      </div>
    </section>
  );
}

function ChannelCard({
  tone,
  icon,
  eyebrow,
  title,
  bullets,
  to,
  cta,
}: {
  tone: 'accent' | 'emerald';
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  bullets: string[];
  to: string;
  cta: string;
}): JSX.Element {
  const accent = tone === 'accent';
  const ring = accent ? 'hover:border-[var(--brand-accent)]/50' : 'hover:border-emerald-500/50';
  const iconCls = accent
    ? 'bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]'
    : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400';
  const checkCls = accent ? 'text-[var(--brand-accent)]' : 'text-emerald-500';
  const glow = accent
    ? 'from-[var(--brand-accent)]/15 to-fuchsia-500/5'
    : 'from-emerald-500/15 to-teal-500/5';

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      className={`group relative flex flex-col overflow-hidden rounded-3xl border border-border bg-card p-8 transition-all hover:shadow-xl ${ring}`}
    >
      <div className={`pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br ${glow} blur-2xl`} />
      <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${iconCls}`}>
        {icon}
      </div>
      <span className="mt-5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {eyebrow}
      </span>
      <h3 className="mt-1 text-xl font-semibold">{title}</h3>
      <ul className="mt-5 space-y-2.5 text-sm">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2.5">
            <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${checkCls}`} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto pt-8">
        <Button asChild variant="outline" className="group/btn">
          <Link to={to}>
            {cta}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
          </Link>
        </Button>
      </div>
    </motion.div>
  );
}
